import type { FC} from 'react';
import { useEffect, useState } from 'react'
import { CheckCircleIcon, XCircleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid'

export type ToastType = 'success' | 'error' | 'info'

interface ToastProps {
  type: ToastType
  message: string
  duration?: number
  onClose: () => void
}

const TOAST_CONFIG = {
  success: {
    icon: CheckCircleIcon,
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    iconColor: 'text-green-500'
  },
  error: {
    icon: XCircleIcon,
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    iconColor: 'text-red-500'
  },
  info: {
    icon: InformationCircleIcon,
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    iconColor: 'text-blue-500'
  }
}

/**
 * Toast notification component
 *
 * Features:
 * - Success, error, and info variants
 * - Auto-dismiss after duration
 * - Slide-in animation from right
 * - Manual close button
 * - Accessible with role="alert" and aria-live
 */
export const Toast: FC<ToastProps> = ({
  type,
  message,
  duration = 3000,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false)

  const config = TOAST_CONFIG[type]
  const Icon = config.icon

  useEffect(() => {
    // Trigger slide-in animation
    const showTimer = setTimeout(() => setIsVisible(true), 10)

    // Auto-dismiss after duration
    const hideTimer = setTimeout(() => {
      setIsVisible(false)
      setTimeout(onClose, 300) // Wait for fade-out animation
    }, duration)

    return () => {
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
    }
  }, [duration, onClose])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 300)
  }

  const visibilityClasses = isVisible
    ? 'opacity-100 translate-x-0'
    : 'opacity-0 translate-x-full'

  return (
    <div
      className={`fixed top-4 right-4 max-w-sm p-4 rounded-lg border shadow-lg z-50 transition-all duration-300 ease-out ${config.bg} ${config.border} ${visibilityClasses}`}
      role="alert"
      aria-live="polite"
      data-testid={`toast-${type}`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 ${config.iconColor}`} aria-hidden="true" />
        <p className={`text-sm font-medium flex-1 ${config.text}`}>{message}</p>
        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          aria-label="Close notification"
          data-testid="toast-close"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}

export default Toast
